import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { KeyRound, X, Lock } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
export const TOKEN_KEY = "zts-admin-token";

const Field = ({ label, value, onChange, testId, autoFocus }) => (
  <label className="block space-y-2">
    <span className="font-mono text-xs tracking-[0.2em] uppercase text-slate-400">{label}</span>
    <input type="password" value={value} onChange={(e) => onChange(e.target.value)} required minLength={8} autoFocus={autoFocus}
      data-testid={testId}
      className="w-full bg-gunmetal border border-steelborder px-4 py-3 text-slate-100 font-body focus:outline-none focus:border-safety transition-colors" />
  </label>
);

export const ChangePasswordDialog = ({ authed, onClose }) => {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (next.length < 8) return setError("Nové heslo musí mať aspoň 8 znakov.");
    if (next !== confirm) return setError("Nové heslá sa nezhodujú.");
    if (next === current) return setError("Nové heslo musí byť iné ako aktuálne.");
    setLoading(true);
    try {
      await authed("/password", { method: "POST", body: JSON.stringify({ current_password: current, new_password: next }) });
      setDone(true);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  return (
    <div data-testid="change-password-dialog" className="fixed inset-0 z-50 bg-ink/80 backdrop-blur-sm grid place-items-center px-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="w-full max-w-sm border border-steelborder bg-steelcard p-8 space-y-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="flex items-center justify-center w-9 h-9 bg-safety text-ink"><KeyRound className="w-4 h-4" /></span>
            <h2 className="font-heading font-bold text-2xl uppercase text-slate-100">Zmeniť heslo</h2>
          </div>
          <button type="button" onClick={onClose} data-testid="change-password-close" aria-label="Zavrieť" className="text-slate-500 hover:text-slate-100 transition-colors"><X className="w-5 h-5" /></button>
        </div>
        {done ? (
          <div className="space-y-5">
            <p data-testid="change-password-success" className="font-mono text-xs text-emerald-400">Heslo bolo zmenené. Pri ďalšom prihlásení použite nové heslo.</p>
            <button type="button" onClick={onClose} className="w-full bg-safety hover:bg-safety-hover text-ink font-heading font-bold uppercase tracking-wider py-3 transition-colors">Hotovo</button>
          </div>
        ) : (
          <>
            <Field label="Aktuálne heslo" value={current} onChange={setCurrent} testId="change-password-current" autoFocus />
            <Field label="Nové heslo (min. 8 znakov)" value={next} onChange={setNext} testId="change-password-new" />
            <Field label="Nové heslo znova" value={confirm} onChange={setConfirm} testId="change-password-confirm" />
            {error && <p data-testid="change-password-error" className="font-mono text-xs text-safety">{error}</p>}
            <button type="submit" disabled={loading} data-testid="change-password-submit"
              className="w-full bg-safety hover:bg-safety-hover disabled:opacity-60 text-ink font-heading font-bold uppercase tracking-wider py-3 transition-colors">
              {loading ? "Ukladám..." : "Uložiť nové heslo"}
            </button>
          </>
        )}
      </form>
    </div>
  );
};

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { document.title = "Nové heslo | ZTS F-Invest"; }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (next.length < 8) return setError("Heslo musí mať aspoň 8 znakov.");
    if (next !== confirm) return setError("Heslá sa nezhodujú.");
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/reset-password`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, new_password: next }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof d.detail === "string" ? d.detail : "Obnova zlyhala.");
      sessionStorage.setItem(TOKEN_KEY, d.access_token);
      navigate("/admin", { replace: true });
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-ink grid place-items-center px-4">
      <form onSubmit={submit} data-testid="reset-password-form" className="w-full max-w-sm border border-steelborder bg-steelcard p-8 space-y-6">
        <div className="flex items-center gap-3">
          <span className="flex items-center justify-center w-9 h-9 bg-safety text-ink"><Lock className="w-4 h-4" /></span>
          <div>
            <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-slate-500">Obnova prístupu</p>
            <h1 className="font-heading font-bold text-2xl uppercase text-slate-100">Nové heslo</h1>
          </div>
        </div>
        {!token ? (
          <p data-testid="reset-password-missing" className="font-mono text-xs text-safety">Odkaz je neúplný. Použite odkaz z e-mailu alebo požiadajte o nový.</p>
        ) : (
          <>
            <Field label="Nové heslo (min. 8 znakov)" value={next} onChange={setNext} testId="reset-password-new" autoFocus />
            <Field label="Nové heslo znova" value={confirm} onChange={setConfirm} testId="reset-password-confirm" />
            {error && <p data-testid="reset-password-error" className="font-mono text-xs text-safety">{error}</p>}
            <button type="submit" disabled={loading} data-testid="reset-password-submit"
              className="w-full bg-safety hover:bg-safety-hover disabled:opacity-60 text-ink font-heading font-bold uppercase tracking-wider py-3 transition-colors">
              {loading ? "Ukladám..." : "Nastaviť heslo a prihlásiť"}
            </button>
          </>
        )}
        <Link to="/admin" data-testid="reset-back-login" className="block text-center font-mono text-xs text-slate-500 hover:text-slate-200 transition-colors">← Späť na prihlásenie</Link>
      </form>
    </div>
  );
}
